import Divider from "@mui/material/Divider";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Typography from "@mui/material/Typography";
import type React from "react";

import type { DispatchField } from "@storyboard/dispatch-view/view";
import type { FilterNode } from "@storyboard/driver-query/predicate";
import { TestIds } from "@storyboard/web-test-ids/testIds";
import { excludeCondition, onlyCondition } from "@/hooks/dispatch/adhocConditions";
import type { DispatchMenuAction, DispatchMenuEntry } from "@/hooks/dispatch/dispatchMenu";
import type { DispatchTableRow } from "@/hooks/dispatch/dispatchTableRow";
import { zinc } from "@/ui/tokens";
import { dispatchMenuItems } from "./dispatchMenuItems";

/**
 * A field the menu can offer conditions on: its key, header text, and how to name a value of it. `valueOnly` says
 * the value's name carries the field's meaning on its own ("Acknowledged"), so the entry reads without the label.
 */
export type RowMenuField = {
  key: DispatchField;
  label: string;
  valueLabel: (value: string) => string;
  valueOnly?: boolean;
};

/** The menu's list lays out on three columns, so each value's line holds its name and its two actions side by side. */
const listGridSx = { display: "grid", gridTemplateColumns: "1fr auto auto", alignItems: "center" } as const;

/** An entry that spans the list's three columns: the acknowledgement and task entries, and the divider. */
const fullWidthSx = { gridColumn: "1 / -1" } as const;

const conditionSx = { minHeight: 0, px: 1, py: 0.25, borderRadius: 0.75, fontSize: "0.75rem" } as const;

/**
 * The row context menu: the acknowledgement and task entries the History menus share, in their order, then after a
 * divider one line per value the row carries ("Truck 156"), with "Only" (keep rows equal to it) and "Exclude" (drop
 * them) as two short actions beside it. The conditions are how a dispatcher adds one the view's controls do not offer;
 * the strip shows it and clears it.
 *
 * The value's name is not an entry, so the arrow keys step from one action to the next past it; each action is named in
 * full ("Only Truck 156") for a screen reader, which hears it out of the line's context.
 *
 * A right-click on a cell is about that cell, so the menu offers the clicked column's value alone. When the right-click
 * missed every cell, or the clicked cell has nothing to condition on, every visible field the row carries a value for
 * is offered instead.
 */
export default function DispatchRowMenu({
  row,
  fields,
  columnKey,
  anchorPosition,
  onClose,
  onAddCondition,
  actions,
  onAction,
}: {
  row: DispatchTableRow | null;
  fields: readonly RowMenuField[];
  /** The field key of the cell right-clicked, or `null` when the menu was opened for the row as a whole. */
  columnKey: string | null;
  anchorPosition: { top: number; left: number } | null;
  onClose: () => void;
  onAddCondition: (condition: FilterNode) => void;
  /** The acknowledgement and task entries for the row, as `rowMenuFor` lists them. */
  actions: readonly DispatchMenuEntry[];
  onAction: (action: DispatchMenuAction) => void;
}) {
  const entriesFor = (candidates: readonly RowMenuField[]) =>
    row
      ? candidates.flatMap((field) => {
          const raw = row.record[field.key];
          const values = Array.isArray(raw) ? raw : raw ? [raw] : [];
          return values.filter((value) => value !== "").map((value) => ({ field, value }));
        })
      : [];
  const scoped = columnKey ? entriesFor(fields.filter((field) => field.key === columnKey)) : [];
  const entries = scoped.length > 0 ? scoped : entriesFor(fields);
  const add = (condition: FilterNode) => {
    onAddCondition(condition);
    onClose();
  };
  // The entry waits a frame: closing the menu hands focus back to the page, and a picker opened in the same commit would
  // lose its autofocused field to that hand-back.
  const pick = (action: DispatchMenuAction) => {
    onClose();
    requestAnimationFrame(() => onAction(action));
  };
  return (
    <Menu
      open={anchorPosition != null && (entries.length > 0 || actions.length > 0)}
      onClose={onClose}
      anchorReference="anchorPosition"
      anchorPosition={anchorPosition ?? undefined}
      slotProps={{
        paper: {
          "data-sb": TestIds.dispatch.rowMenu,
          sx: { bgcolor: zinc[800], color: "white", minWidth: 240 },
        } as React.ComponentProps<typeof Menu>["PaperProps"],
        list: { dense: true, sx: listGridSx },
      }}
    >
      {dispatchMenuItems(actions, pick, fullWidthSx)}
      {actions.length > 0 && entries.length > 0 && <Divider sx={{ ...fullWidthSx, my: 0.5, borderColor: zinc[700] }} />}
      {entries.flatMap(({ field, value }) => {
        const shown = field.valueLabel(value);
        const name = field.valueOnly ? shown : `${field.label} ${shown}`;
        const key = `${field.key}:${value}`;
        return [
          <ConditionName key={`${key}:name`}>
            {field.valueOnly ? (
              <b>{shown}</b>
            ) : (
              <>
                <b>{field.label}</b> {shown}
              </>
            )}
          </ConditionName>,
          <MenuItem
            key={`${key}:only`}
            aria-label={`Only ${name}`}
            onClick={() => add(onlyCondition(field.key, value))}
            sx={{ ...conditionSx, color: "white" }}
          >
            Only
          </MenuItem>,
          <MenuItem
            key={`${key}:exclude`}
            aria-label={`Exclude ${name}`}
            onClick={() => add(excludeCondition(field.key, value))}
            sx={{ ...conditionSx, mr: 0.5, color: zinc[300] }}
          >
            Exclude
          </MenuItem>,
        ];
      })}
    </Menu>
  );
}

/**
 * A value's name at the head of its line. Not an entry: it takes no focus, and `muiSkipListHighlight` keeps the menu
 * from opening with the highlight on it.
 */
function ConditionName({ children }: { children: React.ReactNode }) {
  return (
    <Typography
      component="li"
      role="none"
      sx={{ pl: 2, pr: 1.5, py: 0.5, fontSize: "0.8125rem", whiteSpace: "nowrap" }}
    >
      {children}
    </Typography>
  );
}
ConditionName.muiSkipListHighlight = true;
